import React, { useState, useContext, useEffect } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import axios from 'axios';
import { API_ENDPOINT } from '../../../configurations/global.config';
import { AuthContext } from '../../../contexts/user-context/user-context';
import { OrderType, RerouteMode } from '../../../interfaces/delivery-company/orders';
import { BranchType } from '../../../interfaces/delivery-company/branches';
import CustomDialog from '../custom-dialog/custom-dialog';
import AutocompleteWithThrottle from '../autocomplete-with-throttle/autocomplete-with-throttle';
import { useSnackBar } from '../../../contexts/snackbar';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import RouteIcon from '@mui/icons-material/Route';

interface RerouteOrderDialogProps {
  /** Orders to reroute */
  orders: OrderType[];
  /** Callback when reroute succeeds */
  onSuccess: () => void;
  /** Callback to close dialog */
  onClose: () => void;
  /** Whether the dialog is open */
  open: boolean;
}

/**
 * Dialog for rerouting orders - either for direct delivery from current branch
 * or transfer to a specific branch then delivery from there.
 */
function RerouteOrderDialog({
  orders,
  onSuccess,
  onClose,
  open,
}: RerouteOrderDialogProps) {
  const { config } = useContext(AuthContext);
  const { showMessage } = useSnackBar();

  const [rerouteMode, setRerouteMode] = useState<RerouteMode>(RerouteMode.DIRECT_DELIVERY);
  const [targetBranch, setTargetBranch] = useState<BranchType | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check if any order has a pending transfer (next_leg exists)
  // If no orders have next_leg, they're all at last-mile branch already - hide direct delivery option
  const hasOrdersWithPendingTransfer = orders.some(o => o.next_leg != null);
  const allOrdersAtLastMile = !hasOrdersWithPendingTransfer;

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      // If all orders are already at last mile, default to transfer mode instead
      setRerouteMode(allOrdersAtLastMile ? RerouteMode.TRANSFER_TO_BRANCH : RerouteMode.DIRECT_DELIVERY);
      setTargetBranch(null);
      setError(null);
    }
  }, [open, allOrdersAtLastMile]);

  const handleFetchBranches = async (query: string): Promise<BranchType[]> => {
    try {
      const response = await axios.get<{
        count: number;
        results: BranchType[];
      }>(`${API_ENDPOINT}/api/v1/branches/?name=${encodeURIComponent(query)}`, config);
      return response.data.results;
    } catch (e) {
      console.error('Failed to fetch branches:', e);
      return [];
    }
  };

  // Check if mode requires a target branch
  const requiresTargetBranch = rerouteMode === RerouteMode.TRANSFER_TO_BRANCH || 
                                rerouteMode === RerouteMode.TRANSFER_THEN_ROUTE;

  const handleSubmit = async () => {
    if (requiresTargetBranch && !targetBranch) {
      setError('Please select a target branch');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const requestData: {
        order_ids: string[];
        reroute_mode: RerouteMode;
        target_branch_id?: number | string;
      } = {
        order_ids: orders.map((o) => o.order_id),
        reroute_mode: rerouteMode,
      };

      if (requiresTargetBranch && targetBranch) {
        requestData.target_branch_id = targetBranch.id;
      }

      await axios.post(
        `${API_ENDPOINT}/api/v1/orders/reroute/`,
        requestData,
        config
      );

      showMessage(
        `Successfully rerouted ${orders.length} order${orders.length > 1 ? 's' : ''}`
      );
      onSuccess();
      onClose();
    } catch (e) {
      if (axios.isAxiosError(e)) {
        const errorMessage = e.response?.data?.error || 'Failed to reroute orders';
        setError(errorMessage);
      } else {
        setError('An unexpected error occurred');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Get current branch info from the first order (for display)
  const currentBranch = orders.length > 0 ? orders[0].branch : null;
  const hasMixedBranches = orders.length > 1 && 
    orders.some(o => o.branch?.id !== currentBranch?.id);

  return (
    <CustomDialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AltRouteIcon color="primary" />
          <Typography variant="h6">
            Reroute {orders.length > 1 ? `${orders.length} Orders` : 'Order'}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        {hasMixedBranches && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Selected orders are from different branches. They will all be rerouted 
            based on the selected option.
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {orders.length === 1 ? (
              <>
                Order <strong>{orders[0].order_id}</strong> is currently at{' '}
                <strong>{currentBranch?.name || 'Unknown Branch'}</strong>
                {orders[0].next_leg && orders[0].next_leg.destination ? (
                  <> and scheduled for transfer to{' '}
                    <strong>{orders[0].next_leg.destination.name}</strong>
                  </>
                ) : (
                  <> (last-mile branch - ready for delivery)</>
                )}
              </>
            ) : (
              <>
                {orders.length} orders selected for rerouting
                {allOrdersAtLastMile && ' (all at last-mile branch)'}
              </>
            )}
          </Typography>
        </Box>

        <FormControl component="fieldset" fullWidth>
          <FormLabel component="legend" sx={{ mb: 1 }}>
            Choose reroute option:
          </FormLabel>
          <RadioGroup
            value={rerouteMode}
            onChange={(e) => setRerouteMode(e.target.value as RerouteMode)}
          >
            {/* Only show direct delivery option if at least one order has a pending transfer */}
            {!allOrdersAtLastMile && (
              <FormControlLabel
                value={RerouteMode.DIRECT_DELIVERY}
                control={<Radio />}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocalShippingIcon fontSize="small" color="action" />
                    <Box>
                      <Typography variant="body1">
                        Deliver directly from current branch
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Skip any pending transfers and deliver to customer from here
                      </Typography>
                    </Box>
                  </Box>
                }
                sx={{ 
                  mb: 1, 
                  ml: 0,
                  p: 1.5,
                  border: '1px solid',
                  borderColor: rerouteMode === RerouteMode.DIRECT_DELIVERY ? 'primary.main' : 'divider',
                  borderRadius: 1,
                  backgroundColor: rerouteMode === RerouteMode.DIRECT_DELIVERY ? 'action.selected' : 'transparent',
                }}
              />
            )}
            <FormControlLabel
              value={RerouteMode.TRANSFER_TO_BRANCH}
              control={<Radio />}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SwapHorizIcon fontSize="small" color="action" />
                  <Box>
                    <Typography variant="body1">
                      Transfer to branch, then deliver from there
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Forward to a specific branch for last-mile delivery (no further transfers)
                    </Typography>
                  </Box>
                </Box>
              }
              sx={{ 
                mb: 1,
                ml: 0,
                p: 1.5,
                border: '1px solid',
                borderColor: rerouteMode === RerouteMode.TRANSFER_TO_BRANCH ? 'primary.main' : 'divider',
                borderRadius: 1,
                backgroundColor: rerouteMode === RerouteMode.TRANSFER_TO_BRANCH ? 'action.selected' : 'transparent',
              }}
            />
            <FormControlLabel
              value={RerouteMode.TRANSFER_THEN_ROUTE}
              control={<Radio />}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <RouteIcon fontSize="small" color="action" />
                  <Box>
                    <Typography variant="body1">
                      Transfer to branch, then calculate optimal route
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Forward to a specific branch, then let the system find the best path from there
                    </Typography>
                  </Box>
                </Box>
              }
              sx={{ 
                mb: 1,
                ml: 0,
                p: 1.5,
                border: '1px solid',
                borderColor: rerouteMode === RerouteMode.TRANSFER_THEN_ROUTE ? 'primary.main' : 'divider',
                borderRadius: 1,
                backgroundColor: rerouteMode === RerouteMode.TRANSFER_THEN_ROUTE ? 'action.selected' : 'transparent',
              }}
            />
          </RadioGroup>
        </FormControl>

        {requiresTargetBranch && (
          <Box sx={{ mt: 2 }}>
            <AutocompleteWithThrottle
              label={
                rerouteMode === RerouteMode.TRANSFER_TO_BRANCH 
                  ? "Select Delivery Branch" 
                  : "Select Next Hop Branch"
              }
              required
              fetch={handleFetchBranches}
              value={targetBranch}
              fetchAllOnLoad
              getOptionLabel={(option: BranchType) => option.name}
              onChange={(e, value) => setTargetBranch(value)}
            />
            {rerouteMode === RerouteMode.TRANSFER_THEN_ROUTE && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                The order will be transferred to this branch first, then the system will calculate 
                the optimal route from here to the delivery destination.
              </Typography>
            )}
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
          <Button
            variant="outlined"
            onClick={onClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isLoading || (requiresTargetBranch && !targetBranch)}
            startIcon={isLoading ? <CircularProgress size={16} /> : <AltRouteIcon />}
          >
            {isLoading ? 'Rerouting...' : 'Reroute'}
          </Button>
        </Box>
      </DialogContent>
    </CustomDialog>
  );
}

export default RerouteOrderDialog;

